import "./css/site.css";
import type { GameHandle } from "./game/index.js";
import { closeClockDetail, getActiveClock, openClockDetail, type ClockKind } from "./site/clock-detail.js";
import { startClockWidgets } from "./site/clock-widgets.js";
import { applyMeta, navigate } from "./site/meta.js";
import { initTerminal } from "./site/terminal.js";
import { parseHms } from "./site/time.js";

const INTERNAL_HOSTS = new Set(["joshammer.com", "www.joshammer.com"]);
/** Matches the CSS transition on #game-container. */
const EXPAND_MS = 500;

const container = document.getElementById("game-container") as HTMLElement;
const sidebar = document.getElementById("sidebar") as HTMLElement;

let game: GameHandle | null = null;
/** Set when the player asks for the game before the engine chunk has loaded. */
let pendingEnter: { instant: boolean } | null = null;
let fullscreen = false;
let animating = false;
let insetRect: DOMRect | null = null;

/** Height the inset game occupies, matching the responsive CSS breakpoints. */
function insetHeight(): string {
  if (window.innerWidth <= 600) return "260px";
  if (window.innerWidth <= 1100) return "320px";
  return "350px";
}

function resizeGame(): void {
  game?.resize(container.clientWidth, container.clientHeight);
}

function ensurePlaceholder(): HTMLElement {
  let placeholder = document.getElementById("game-placeholder");
  if (placeholder) return placeholder;

  placeholder = document.createElement("div");
  placeholder.id = "game-placeholder";
  placeholder.style.width = "100%";
  placeholder.style.height = insetHeight();
  sidebar.insertBefore(placeholder, container);
  return placeholder;
}

function ensureExitButton(): HTMLElement {
  let button = document.getElementById("game-exit-btn");
  if (button) return button;

  button = document.createElement("button");
  button.id = "game-exit-btn";
  button.textContent = "ESC to exit";
  button.addEventListener("click", () => exitGame());
  document.body.appendChild(button);
  return button;
}

function clearInlineBounds(): void {
  for (const property of ["top", "left", "right", "width", "height", "transform"] as const) {
    container.style[property] = "";
  }
}

export function enterGame(instant = false): void {
  if (fullscreen || animating) return;
  if (!game) {
    pendingEnter = { instant };
    return;
  }

  navigate("/grapple");
  document.documentElement.classList.remove("init-grapple");

  insetRect = container.getBoundingClientRect();
  ensurePlaceholder();
  document.body.appendChild(container);
  document.body.style.overflow = "hidden";

  const finish = () => {
    container.classList.remove("expanding");
    clearInlineBounds();
    fullscreen = true;
    animating = false;
    game?.setFocused(true);
    resizeGame();
  };

  if (instant) {
    container.classList.add("fullscreen");
    finish();
  } else {
    animating = true;
    Object.assign(container.style, {
      top: `${insetRect.top}px`,
      left: `${insetRect.left}px`,
      right: "auto",
      width: `${insetRect.width}px`,
      height: `${insetRect.height}px`,
      transform: "none",
    });
    container.classList.add("expanding");

    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        container.classList.add("fullscreen");
        // The transition can be interrupted, so a timeout guarantees the
        // handler runs exactly once either way.
        onTransitionEnd(container, "width", finish, EXPAND_MS);
      }),
    );
  }

  window.setTimeout(() => ensureExitButton().classList.add("visible"), 300);
}

/** Shrinks the game back into the sidebar, landing on `to`. */
export function exitGame(to = "/"): void {
  if (!fullscreen || animating) return;
  animating = true;

  navigate(to);
  document.getElementById("game-exit-btn")?.classList.remove("visible");
  document.body.style.overflow = "";
  game?.setFocused(false);

  const placeholder = document.getElementById("game-placeholder");
  const target = placeholder?.getBoundingClientRect() ?? insetRect ?? fallbackRect();

  Object.assign(container.style, {
    top: "0px",
    left: "0px",
    right: "auto",
    width: "100vw",
    height: "100vh",
    transform: "none",
  });
  container.classList.add("expanding");
  container.classList.remove("fullscreen");

  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      Object.assign(container.style, {
        top: `${target.top}px`,
        left: `${target.left}px`,
        width: `${target.width}px`,
        height: `${target.height}px`,
      });

      onTransitionEnd(
        container,
        "width",
        () => {
          container.classList.remove("expanding");
          clearInlineBounds();
          if (placeholder?.parentNode) placeholder.parentNode.replaceChild(container, placeholder);
          else sidebar.prepend(container);
          fullscreen = false;
          animating = false;
          resizeGame();
        },
        EXPAND_MS,
      );
    }),
  );
}

function fallbackRect(): { top: number; left: number; width: number; height: number } {
  const bounds = sidebar.getBoundingClientRect();
  const width = Math.min(bounds.width, 520);
  return {
    top: bounds.top,
    left: bounds.left + (bounds.width - width) / 2,
    width,
    height: width * (380 / 520),
  };
}

/** Runs `done` on the named transition or after `timeout`, whichever is first. */
function onTransitionEnd(
  element: HTMLElement,
  property: string,
  done: () => void,
  timeout: number,
): void {
  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    element.removeEventListener("transitionend", listener);
    done();
  };
  const listener = (event: TransitionEvent) => {
    if (event.target !== element || event.propertyName !== property) return;
    finish();
  };

  element.addEventListener("transitionend", listener);
  window.setTimeout(finish, timeout + 120);
}

export const isGameFullscreen = (): boolean => fullscreen;

// ------------------------------------------------------------------- routing

function isClockPath(path: string): ClockKind | null {
  if (path === "/chron" || path.startsWith("/chron/")) return "chron";
  if (path === "/duod" || path.startsWith("/duod/")) return "duod";
  return null;
}

function deepLinkTime(path: string, kind: ClockKind): string | undefined {
  const prefix = `/${kind}/`;
  if (!path.startsWith(prefix)) return undefined;
  const raw = decodeURIComponent(path.slice(prefix.length));
  return parseHms(raw) === null ? undefined : raw;
}

function openInternal(path: string): void {
  const clock = isClockPath(path);
  if (clock) {
    openClockDetail(clock, { deepLink: deepLinkTime(path, clock) });
    navigate(clock === "chron" ? "/chron" : "/duod");
    return;
  }
  if (path === "/grapple") {
    enterGame();
    return;
  }
  // Anything else on this site is a route the router already knows how to
  // open; pushing the path alone would change the URL and nothing else.
  navigate(path);
  applyRoute(path, false);
}

/** A link box in the game: internal links stay in the SPA, others open a tab. */
function handleGameLink(url: string): void {
  if (url === "__EXIT_GAME__") {
    exitGame();
    return;
  }

  let internalPath: string | null = null;
  try {
    const parsed = new URL(url, window.location.origin);
    if (INTERNAL_HOSTS.has(parsed.hostname) || parsed.origin === window.location.origin) {
      internalPath = parsed.pathname;
    }
  } catch {
    internalPath = null;
  }

  if (internalPath) {
    // The URL changes as the game shrinks rather than passing through "/",
    // which would leave a stop nobody asked for in the history.
    exitGame(internalPath);
    window.setTimeout(() => openInternal(internalPath), EXPAND_MS);
    return;
  }

  window.open(url, "_blank", "noopener");
}

function applyRoute(path: string, initial: boolean): void {
  applyMeta(path);

  const standalone = isWotPath(path) || isTttPath(path);
  // Each of these is a page of its own and covers the screen. The fullscreen
  // game is drawn above everything, so it has to come down first — otherwise
  // going Back from the game to one of them lands on a page you cannot see.
  if (standalone && fullscreen) exitGame(path);

  if (isWotPath(path)) {
    void openWot();
    return;
  }
  closeWot();

  if (isTttPath(path)) {
    void openTtt();
    return;
  }
  closeTtt();

  const clock = isClockPath(path);
  if (clock) {
    openClockDetail(clock, { instant: initial, deepLink: deepLinkTime(path, clock) });
  } else if (getActiveClock()) {
    closeClockDetail();
  }

  if (path === "/grapple") {
    if (!fullscreen) enterGame(initial);
  } else if (fullscreen) {
    exitGame();
  }
}

// ---------------------------------------------------------------- wot survey

const isWotPath = (path: string): boolean => path === "/wot" || path === "/wot/";

let wotHost: HTMLElement | null = null;

/**
 * The survey is a page of its own rather than a panel over the terminal, and
 * it is the only thing on screen while it is open. Loaded on demand: it carries
 * the whole character roster and nobody who came for the terminal should pay
 * for that.
 */
async function openWot(): Promise<void> {
  if (wotHost) return;

  document.documentElement.classList.add("init-wot");
  const host = document.createElement("div");
  host.id = "wot-host";
  document.body.appendChild(host);
  wotHost = host;

  const { WotPage } = await import("./wot/page.js");
  const page = new WotPage();
  host.appendChild(page.root);
  await page.start();
}

function closeWot(): void {
  document.documentElement.classList.remove("init-wot");
  if (!wotHost) return;
  wotHost.remove();
  wotHost = null;
}

// ------------------------------------------------------------------ the cube

const isTttPath = (path: string): boolean => path === "/ttt" || path === "/ttt/";

let tttHost: HTMLElement | null = null;
let tttPage: { destroy(): void } | null = null;

/**
 * The board game, loaded on demand like the survey. It has its own canvas and
 * its own idea of the whole screen, so the terminal steps aside for it.
 */
async function openTtt(): Promise<void> {
  if (tttHost) return;

  document.documentElement.classList.add("init-ttt");
  const host = document.createElement("div");
  host.id = "ttt-host";
  document.body.appendChild(host);
  tttHost = host;

  const { TttPage } = await import("./ttt/page.js");
  const page = new TttPage();
  host.appendChild(page.root);
  page.start();
  tttPage = page;
}

function closeTtt(): void {
  document.documentElement.classList.remove("init-ttt");
  if (!tttHost) return;
  tttPage?.destroy();
  tttPage = null;
  tttHost.remove();
  tttHost = null;
}

// --------------------------------------------------------------------- boot

initTerminal();
const stopWidgets = startClockWidgets((kind) => {
  openClockDetail(kind as ClockKind);
  navigate(kind === "chron" ? "/chron" : "/duod");
});

// Phaser is by far the largest thing the page loads, and the terminal is
// readable without it, so the engine streams in behind the first paint. Any
// request to enter the game before it lands is remembered and honoured.
void import("./game/index.js")
  .then(({ startGame }) => {
    game = startGame({ parent: container, onOpenLink: handleGameLink });
    game.onRequestFocus(() => {
      if (!fullscreen) enterGame();
    });
    resizeGame();

    if (pendingEnter) {
      const { instant } = pendingEnter;
      pendingEnter = null;
      if (fullscreen) game.setFocused(true);
      else enterGame(instant);
    }
  })
  .catch((error) => {
    console.error("[game] engine failed to load:", error);
    document.documentElement.classList.remove("init-grapple");
    pendingEnter = null;
  });

applyRoute(window.location.pathname, true);

window.addEventListener("popstate", () => applyRoute(window.location.pathname, false));
window.addEventListener("resize", resizeGame);

window.addEventListener("keydown", (event) => {
  if (event.key !== "Escape") return;
  if (game?.isOverlayOpen()) return;
  if (fullscreen) exitGame();
  else if (getActiveClock()) closeClockDetail();
});

document.getElementById("clock-exit-btn")?.addEventListener("click", () => {
  closeClockDetail();
  navigate("/");
});

window.addEventListener("beforeunload", () => {
  stopWidgets();
  game?.destroy();
});
